import { Segmented } from './Select';
export const PAGE_SIZES = [25, 50, 100] as const;

interface PaginatorProps {
  page: number;
  perPage: number;
  total: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  busy?: boolean;
}

/**
 * Page controls for a server-paginated table.
 *
 * Replaces the "and N more" dead-ends that used to end every list. Those were worse than a plain
 * truncation: they told the reader more data existed and gave them no way to reach it — and the
 * server capped every breakdown at 20 rows anyway, so no client control could have.
 */
export function Paginator({
  page, perPage, total, onPageChange, onPerPageChange, busy = false,
}: PaginatorProps) {
  const pages = Math.max(1, Math.ceil(total / perPage));
  const first = total === 0 ? 0 : (page - 1) * perPage + 1;
  const last = Math.min(page * perPage, total);

  return (
    <div className="paginator">
      <span className="t-label muted">
        {total === 0 ? 'No rows' : `Showing ${first}–${last} of ${total.toLocaleString('en-US')}`}
      </span>

      <div className="paginator-controls">
        {/* Segmented, not a dropdown: three short numeric choices where seeing the
            alternatives is the whole point. */}
        <Segmented
          label="Rows"
          inline
          value={perPage}
          options={PAGE_SIZES.map((s) => ({ value: s, label: String(s) }))}
          onChange={(size) => {
            // Changing page size must reset to page 1 — page 4 of a 25-row paging can be past
            // the end at 100 rows, which would show an empty table for no visible reason.
            onPerPageChange(size);
            onPageChange(1);
          }}
        />

        <button
          className="btn btn-ghost"
          onClick={() => onPageChange(page - 1)}
          disabled={busy || page <= 1}
        >
          ← Prev
        </button>
        <span className="t-label" style={{ minWidth: 72, textAlign: 'center' }}>
          {page} / {pages}
        </span>
        <button
          className="btn btn-ghost"
          onClick={() => onPageChange(page + 1)}
          disabled={busy || page >= pages}
        >
          Next →
        </button>
      </div>
    </div>
  );
}
